import React, { FC, useRef } from "react";
import {
  Clock,
  Upload,
  Eye,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Play,
} from "lucide-react";
import { useUploadFibiDs160 } from "../../../../../../hooks/ai/useAI";
import { toast } from "react-toastify";
import { useSnapshot } from "valtio";
import { userDataStore } from "../../../../../../store/userData";
import { AiOutlineComment } from "react-icons/ai";
import useOpener from "../../../../../../hooks/useOpener";
import { TourModal } from "./TourModal";

interface Props {
  onStart: () => void;
}

const BusinessStarter: FC<Props> = ({ onStart }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const { userData } = useSnapshot(userDataStore);
  const { mutate, isLoading } = useUploadFibiDs160();
  const { open, onClose, onOpen } = useOpener();

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const temp = e.target.files[0];
      const formData = new FormData();
      formData.append("file", temp);
      mutate(formData, {
        onSuccess: () => {
          toast.success("Upload Successful!");
        },
      });
    }
  };

  const uploadedFile = userData?.userInfo.ds160URL;

  const handleViewDS160 = () => {
    if (uploadedFile) {
      window.open(uploadedFile, "_blank");
    }
  };

  return (
    <>
      <div className="w-full h-full overflow-y-auto ">
        <div className="flex flex-col w-full h-full gap-10  md:w-[95%] xl:w-[80%] mx-auto">
          {/* Header */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 mb-4 shadow-lg bg-linear-to-r from-blue-600 to-indigo-600 rounded-2xl">
              <AiOutlineComment className="w-8 h-8 text-white" />
              {/* <Sparkles className="w-8 h-8 text-white" /> */}
            </div>
            <h1 className="mb-2 text-3xl font-bold text-gray-900">
              {userData?.userInfo.name.split(" ")[0]}, welcome to FiBi’s
              Interview Simulation.
            </h1>
            <p className="text-lg text-gray-600">
              Practice makes perfect. Let's get you ready.
            </p>
          </div>

          <div className="text-center">
            <button
              disabled={isLoading}
              onClick={onOpen}
              // onClick={onStart}
              className="inline-flex items-center gap-3 px-12 py-4 text-lg font-semibold text-white transition-all duration-200 transform shadow-xl bg-[#0076E9] rounded-2xl hover:scale-105 hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-lg"
            >
              <Play className="w-6 h-6" />
              Start Simulation
            </button>
          </div>

          {/*  */}
          <div className="p-6 transition-all duration-200 border-2 border-gray-200 border-dashed rounded-2xl hover:border-blue-300 hover:bg-blue-50/30">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h4 className="mb-2 font-semibold text-gray-900">
                  Optional: Upload Your DS-160 Form
                </h4>
                <p className="max-w-md mx-auto mb-6 text-sm text-gray-600">
                  Upload your DS-160 form to personalize your simulation
                  experience. You can update this anytime before starting.
                </p>
              </div>

              {uploadedFile ? (
                <div className="p-4 border border-green-200 bg-green-50 rounded-xl">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">
                      File Uploaded Successfully
                    </span>
                  </div>

                  <div className="flex items-center justify-center gap-3">
                    <button
                      onClick={handleViewDS160}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-700 transition-colors bg-blue-100 rounded-lg hover:bg-blue-200"
                    >
                      <Eye className="w-4 h-4" />
                      View File
                    </button>
                    <button
                      onClick={() => fileRef.current?.click()}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 transition-colors bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      <RefreshCw className="w-4 h-4" />
                      Replace File
                    </button>
                  </div>
                </div>
              ) : isLoading ? (
                <div className="flex items-center justify-center gap-3 py-4">
                  <div className="w-5 h-5 border-2 border-blue-600 rounded-full animate-spin border-t-transparent"></div>
                  <span className="font-medium text-blue-600">
                    Uploading...
                  </span>
                </div>
              ) : (
                <button
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-2 px-6 py-3 font-medium text-white transition-all duration-200 transform shadow-lg bg-linear-to-r from-[#0076E9] to-blue-600 rounded-xl hover:from-[#0076E9] hover:to-[#0076E9] hover:shadow-xl"
                >
                  <Upload className="w-5 h-5" />
                  Choose File
                </button>
              )}

              <input
                ref={fileRef}
                type="file"
                accept=".pdf"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
          </div>

          <div className="flex flex-col gap-4 sm:flex-row">
            {/*  */}
            <div className="flex items-start w-full gap-4 p-6 border border-blue-100 bg-blue-50 rounded-2xl">
              <div className="flex items-center justify-center shrink-0 w-10 h-10 bg-blue-100 rounded-xl">
                <Clock className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="mb-2 font-semibold text-gray-900">
                  How it works
                </h3>
                <p className="leading-relaxed text-gray-700">
                  Prepare for your interview with this timed 10-minute
                  simulation. FiBi will provide helpful suggestions during each
                  question, giving you insight into how to best respond in a
                  professional setting.
                </p>
              </div>
            </div>

            {/*  */}
            <div className="flex items-start w-full gap-4 p-6 border bg-amber-50 rounded-2xl border-amber-200">
              <div className="flex items-center justify-center shrink-0 w-10 h-10 bg-amber-100 rounded-xl">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div className="flex-1">
                <h3 className="mb-2 font-semibold text-gray-900">
                  Important Notice
                </h3>
                <p className="leading-relaxed text-gray-700">
                  This simulation is 10 minutes long, which is longer than a
                  typical real-world interview. Once started, you cannot pause
                  or take breaks. Completing the entire session is required and
                  will count towards your total practice sessions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {open && <TourModal isOpen={open} onClose={onClose} onStart={onStart} />}
    </>
  );
};

export default BusinessStarter;
